import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { BookButton } from "@/components/ui-bits";
import NotFound from "@/pages/NotFound";
import { api } from "@/lib/api";
import { HOTEL } from "@/lib/site";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    setPost(undefined);
    api.getPost(slug).then((r) => setPost(r.data)).catch(() => setPost(null));
  }, [slug]);

  if (post === null) return <NotFound />;
  if (post === undefined)
    return <div className="pt-40 pb-24 text-center text-stone">Loading…</div>;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    image: post.cover_image,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: HOTEL.name },
    publisher: { "@type": "Organization", name: HOTEL.name },
    description: post.excerpt,
  };

  return (
    <>
      <Seo
        title={`${post.seo_title || post.title} | Regency Inn & Suites`}
        description={post.seo_description || post.excerpt}
        path={`/local-guide/${post.slug}`}
        image={post.cover_image}
        type="article"
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Local Guide", path: "/local-guide" },
            { name: post.title, path: `/local-guide/${post.slug}` },
          ]),
          articleSchema,
        ]}
      />
      <article className="bg-bone">
        <div className="relative h-[52vh] min-h-[380px] overflow-hidden">
          <img src={post.cover_image} alt={post.title} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/assets/hotel/g6.jpg"; }} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/85 to-forest-deep/20" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-3xl mx-auto px-6 pb-14 w-full">
              <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-amber mb-4">
                <span>{post.category}</span>
                <span className="text-bone/70">· {post.read_time} min read</span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-bone leading-[1.05]">{post.title}</h1>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-16 lg:py-20">
          <nav className="text-xs uppercase tracking-widest text-stone flex gap-2 mb-10" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber">Home</Link>/
            <Link to="/local-guide" className="hover:text-amber">Local Guide</Link>
          </nav>
          <p className="font-serif text-2xl text-forest leading-snug mb-8">{post.excerpt}</p>
          <div className="prose-editorial" dangerouslySetInnerHTML={{ __html: post.content }} />

          <div className="mt-16 rounded-md bg-sand p-10 text-center">
            <h2 className="font-serif text-3xl text-forest">Planning a trip to Pittsburg?</h2>
            <p className="mt-3 text-stone">Book direct at Regency Inn & Suites for our best available rate.</p>
            <div className="mt-6 flex justify-center">
              <BookButton label="Book Your Stay" testid="blogpost-book" />
            </div>
          </div>
        </div>
      </article>
    </>
  );
}
